import { useState, useEffect } from 'react'
import { Clock, AlertTriangle, X } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useTeamContext } from '../context/TeamContext'
import UpgradeModal from './UpgradeModal'

/**
 * Subscription status banner that shows warnings for:
 * - Active trial with countdown (soft info bar)
 * - Trial expired (non-dismissible copper banner)
 * - Past due payments
 * - Cancelled subscriptions
 *
 * Displays at the top of the layout. Reads from useAuth() context.
 */
export default function SubscriptionBanner() {
  const { subscriptionStatus, trialEndsAt, loading } = useAuth()
  const { isTeamMember } = useTeamContext()
  const [dismissed, setDismissed] = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)

  // Reset dismissed state when status changes
  useEffect(() => {
    setDismissed(false)
  }, [subscriptionStatus])

  if (loading) return null

  // Team members operate under the agency's subscription — hide subscription UI
  if (isTeamMember) return null

  // No banner for active subscribers or non-landlord/agent users
  if (!subscriptionStatus || subscriptionStatus === 'active') return null

  // Calculate trial days remaining
  const trialDaysRemaining = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0

  // Trial expired — non-dismissible copper banner
  if (subscriptionStatus === 'trial_expired') {
    return (
      <>
        <div className="px-4 py-2 z-50" style={{ backgroundColor: '#c87941', color: '#1a2744' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>
                Your free trial has ended. Your account is now <strong>read-only</strong>.
              </span>
            </div>
            <button
              onClick={() => setShowUpgrade(true)}
              className="px-3 py-1 rounded-lg text-sm font-medium transition hover:opacity-90 cursor-pointer"
              style={{ backgroundColor: '#1a2744', color: 'white' }}
            >
              Choose a Plan
            </button>
          </div>
        </div>
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
      </>
    )
  }

  // Active trial — standalone banner
  if (subscriptionStatus === 'trial' && trialDaysRemaining > 0) {
    if (dismissed) return null

    return (
      <>
        <div className="px-4 py-3" style={{ backgroundColor: '#1a2744' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'rgba(200, 121, 65, 0.2)' }}
              >
                <Clock className="w-4 h-4" style={{ color: '#c87941' }} />
              </div>
              <div>
                <p className="text-sm font-medium text-white">
                  Free trial: <strong>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</strong> remaining
                </p>
                <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Full access to all features
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUpgrade(true)}
                className="px-4 py-1.5 rounded-lg text-sm font-medium transition hover:opacity-90 cursor-pointer"
                style={{ backgroundColor: '#c87941', color: 'white' }}
              >
                Choose a Plan
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="p-1 rounded transition hover:opacity-80 cursor-pointer"
                style={{ color: 'rgba(255, 255, 255, 0.4)' }}
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
      </>
    )
  }

  // Trial ending today
  if (subscriptionStatus === 'trial' && trialDaysRemaining === 0) {
    return (
      <>
        <div className="bg-red-500 text-white px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>
                Your trial ends <strong>today</strong>. Subscribe now to keep full access.
              </span>
            </div>
            <button
              onClick={() => setShowUpgrade(true)}
              className="px-3 py-1 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 transition cursor-pointer"
            >
              Subscribe Now
            </button>
          </div>
        </div>
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
      </>
    )
  }

  // Past due payment
  if (subscriptionStatus === 'past_due') {
    if (dismissed) return null

    return (
      <div className="bg-red-500 text-white px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              Your payment failed. Please update your payment method to avoid losing access.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/settings/billing"
              className="px-3 py-1 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 transition"
            >
              Update Payment
            </a>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 rounded hover:bg-white/20 transition"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Cancelled subscription
  if (subscriptionStatus === 'cancelled') {
    if (dismissed) return null

    return (
      <>
        <div className="px-4 py-2" style={{ backgroundColor: '#c87941', color: '#1a2744' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>
                Your subscription has been cancelled. Subscribe to regain full access.
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUpgrade(true)}
                className="px-3 py-1 rounded-lg text-sm font-medium transition hover:opacity-80 cursor-pointer"
                style={{ backgroundColor: 'rgba(26, 39, 68, 0.15)' }}
              >
                Subscribe
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="p-1 rounded transition"
                style={{ color: '#1a2744', opacity: 0.6 }}
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
      </>
    )
  }

  return null
}
